<template>
    <div class="flex flex-col h-full">
        <div class="px-2 pt-2">
            <vx-alert data-test="group-alert" color="info" :closeable="false" shadow>
                <template v-slot:icon>
                    <font-awesome-icon :icon="faDiamondExclamation" widthAuto></font-awesome-icon>
                </template>
                <template v-if="ouFilteredContactGroups.length > 1">
                    This contact is in several groups. <strong>Choose a group to continue.</strong>
                </template>
                <template v-else-if="ouFilteredContactGroups.length === 1">
                    This contact is in a group. <strong>Choose a group to continue.</strong>
                </template>
                <template v-else> This contact is not in any group in this OU. </template>
            </vx-alert>

            <div class="mt-2">
                <vx-input
                    data-test="group-search"
                    size="lg"
                    class="shadow-sm"
                    v-model="search"
                    maxlength="40"
                    ref="searchInputRef"
                    placeholder="Search"
                    block
                    @keydown="onListKeyDown"
                >
                    <template v-slot:right>
                        <font-awesome-icon widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
        </div>

        <div v-if="loadingGroups" class="flex-1 flex items-center justify-center">
            <vx-logo-loading />
        </div>
        <template v-else-if="filteredGroups.length === 0 && search">
            <div data-test="no-results" class="flex-1 flex items-center justify-center p-4">
                <span class="text-slate-500 text-center text-sm">No results found with "{{ search }}"</span>
            </div>
        </template>
        <template v-else-if="filteredGroups.length === 0">
            <div data-test="no-groups" class="flex-1 flex items-center justify-center p-4">
                <vx-box
                    color="info"
                    class="w-full flex flex-col px-8 py-12 gap-4 items-center text-base text-slate-500 text-center"
                >
                    No groups available in this Organizational Unit.
                </vx-box>
            </div>
        </template>
        <div v-else class="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
            <div
                v-for="(group, index) in filteredGroups"
                :key="group.group_id"
                data-test="group-option"
                :ref="(el) => (optionsRef[index] = el)"
                @click="onSelectGroup(group.group_id)"
                @keydown.enter="onSelectGroup(group.group_id)"
                @mouseenter="
                    () => {
                        if (!disableMouseIndexChange) {
                            activeIndex = index;
                        }
                    }
                "
                @mousemove="disableMouseIndexChange = false"
                class="flex items-center justify-between rounded-[14px] py-2 px-3 cursor-pointer outline-hidden border border-solid"
                :class="{
                    'border-sky-200 bg-white': localSelectedGroupId === group.group_id,
                    'bg-white border-slate-100': localSelectedGroupId !== group.group_id,
                    'bg-sky-50!': activeIndex === index,
                }"
            >
                <div class="flex items-center gap-2 flex-row">
                    <vx-radio :modelValue="localSelectedGroupId === group.group_id" />
                    <div>
                        <div class="text-sm font-bold text-slate-700 line-clamp-1">{{ group.name }}</div>
                        <div class="text-xs text-slate-500 break-all">{{ group.group_id }}</div>
                    </div>
                </div>
                <vx-badge v-if="isPreferred(group)" data-test="preferred-badge" size="xs" color="violet">
                    Preferred
                    <font-awesome-icon widthAuto :icon="faStar" />
                </vx-badge>
            </div>
        </div>

        <div class="sticky inset-be-0 bg-slate-100 border-0 border-t border-solid border-slate-200 p-6 flex gap-3">
            <vx-button data-test="cancel-button" color="muted" size="xl" @click="emit('cancel')"> Cancel </vx-button>
            <vx-button
                data-test="continue-button"
                class="flex-1"
                color="primary"
                size="xl"
                :disabled="!localSelectedGroupId"
                @click="onContinue"
            >
                Continue
                <font-awesome-icon widthAuto :icon="faArrowRight" />
            </vx-button>
        </div>
    </div>
</template>
<script setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import { VxBox, VxRadio, VxBadge, VxLogoLoading, VxAlert, VxInput, VxButton } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faStar, faDiamondExclamation, faMagnifyingGlass, faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { useMessageHub } from '~/components/message-hub/useMessageHub';

const { groups, loadingGroups } = useMessageHub();

const props = defineProps({
    contactGroups: {
        type: Array,
        required: true,
    },
    selectedGroupId: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['select', 'cancel', 'update:selectedGroupId']);

const ouFilteredContactGroups = computed(() => {
    const ouGroupIds = new Set(groups.value.map((g) => g.group_id));
    return props.contactGroups.filter((cg) => ouGroupIds.has(cg.group_id));
});

const search = ref('');
const localSelectedGroupId = ref(props.selectedGroupId);
const activeIndex = ref(0);
const disableMouseIndexChange = ref(false);
const optionsRef = ref([]);
const searchInputRef = ref();

watch(
    () => props.selectedGroupId,
    (val) => {
        localSelectedGroupId.value = val;
    }
);

onMounted(() => {
    searchInputRef.value?.field?.focus();
});

const onSelectGroup = (groupId) => {
    localSelectedGroupId.value = groupId;
    emit('update:selectedGroupId', groupId);
};

const onContinue = () => {
    if (localSelectedGroupId.value) {
        emit('select', { groupId: localSelectedGroupId.value });
    }
};

const onListKeyDown = (event) => {
    if (!filteredGroups.value.length) {
        return;
    }

    if (event.key === 'ArrowDown') {
        disableMouseIndexChange.value = true;
        event.preventDefault();
        activeIndex.value = activeIndex.value < filteredGroups.value.length - 1 ? activeIndex.value + 1 : 0;
        scrollToActive();
    } else if (event.key === 'ArrowUp') {
        disableMouseIndexChange.value = true;
        event.preventDefault();
        activeIndex.value = activeIndex.value > 0 ? activeIndex.value - 1 : filteredGroups.value.length - 1;
        scrollToActive();
    } else if (event.key === 'Enter') {
        event.preventDefault();
        const group = filteredGroups.value[activeIndex.value];
        if (group) {
            if (localSelectedGroupId.value === group.group_id) {
                onContinue();
            } else {
                onSelectGroup(group.group_id);
            }
        }
    }
};

const scrollToActive = async () => {
    await nextTick();
    const el = optionsRef.value[activeIndex.value];
    if (el?.$el) {
        el.$el.scrollIntoView?.({ block: 'center' });
    } else if (el?.scrollIntoView) {
        el.scrollIntoView({ block: 'center' });
    }
};

watch(search, async () => {
    activeIndex.value = 0;
    await nextTick();
    scrollToActive();
});

const isPreferred = (group) => {
    return props.contactGroups.some((cg) => cg.group_id === group.group_id && cg.is_preferred === true);
};

const sortedGroups = computed(() => {
    const preferred = groups.value.filter((g) => isPreferred(g));
    const rest = groups.value.filter((g) => !isPreferred(g));
    return [...preferred, ...rest];
});

const filteredGroups = computed(() => {
    if (!search.value.length) {
        return sortedGroups.value;
    }

    return sortedGroups.value.filter((group) => {
        const searchLower = search.value.toLowerCase();
        return group.name.toLowerCase().includes(searchLower) || group.group_id.toLowerCase().includes(searchLower);
    });
});
</script>
